import { forwardRef } from 'react';
import { View } from 'react-native';

import { Text, VStack, theme } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  DateFormat,
  formatDate,
  isDatePastInTimezone,
  parseDateWithTimezone,
} from '@/utils/dateUtils';

import { styles } from './styles';
import type { CancellationPolicyProps } from './types';

const CancellationPolicy = forwardRef<View, CancellationPolicyProps>(
  (
    {
      testID = 'cancellation-policy',
      bookingInfo,
      timezone,
      variant = 'booking',
      ...props
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('CancellationPolicy');

    const cancellationDate =
      variant !== 'giftCard' && bookingInfo?.canCancelBefore
        ? parseDateWithTimezone(
            bookingInfo.canCancelBefore,
            timezone || 'America/New_York',
          )
        : undefined;

    const isCancellationWindowPassed = cancellationDate
      ? isDatePastInTimezone(cancellationDate, timezone || 'America/New_York')
      : false;

    const cancelTime =
      cancellationDate && !isCancellationWindowPassed
        ? formatDate(cancellationDate, DateFormat.TIME)
        : '';
    const cancelDate =
      cancellationDate && !isCancellationWindowPassed
        ? formatDate(cancellationDate, DateFormat.DATE)
        : '';

    const description =
      variant === 'giftCard'
        ? t('cannotCancelDescriptionGiftCard')
        : !cancellationDate || isCancellationWindowPassed
          ? t('cannotCancelDescription')
          : t('freeCancellation', {
              time: cancelTime,
              date: cancelDate,
            });

    return (
      <VStack ref={ref} testID={testID} {...props} space={theme.spacing[4]}>
        <View style={styles.divider} />
        <Text variant="lStrong" color="textPrimary">
          {t('title')}
        </Text>
        <Text variant="m" color="textPrimary">
          {description}
        </Text>
        <View style={styles.divider} />
      </VStack>
    );
  },
);

CancellationPolicy.displayName = 'CancellationPolicy';
export { CancellationPolicy };
